import Alpine from "alpinejs";
import { EditorStore } from "./store";

// Editor Store - Strategy Pattern
Alpine.store("editor", {
  // --- State ---
  delegate: null,
  loaded: false,
  i18n: null,
  status: "saved",
  tocHTML: "",
  linkModalOpen: false,
  linkUrl: "",
  activeMarks: {
    bold: false,
    italic: false,
    underline: false,
    highlight: false,
    link: false,
    heading1: false,
    heading2: false,
    heading3: false,
    bulletList: false,
    orderedList: false,
    textAlignLeft: false,
    textAlignCenter: false,
    textAlignRight: false,
    textAlignJustify: false,
    canMergeCells: false,
    canSplitCell: false,
    isTableActive: false,
  },
  activeColor: "#000000",
  manualFontSize: "",
  history: { canUndo: false, canRedo: false },
  activeNodes: {},
  stats: { words: 0, chars: 0 },

  // --- Delegated Methods ---
  mount(el: HTMLElement, cfg: any) {
    if (!this.delegate) {
      console.error("Store delegate missing during mount! Check editor.iife.js loading.");
      // Fallback: try to set i18n anyway to show status if possible
      if (cfg && cfg.i18n) {
        this.i18n = cfg.i18n;
      }
    }
    this.delegate?.mount(el, cfg);
  },

  // STRICT TYPING HERE: Prevents "dropped argument" bugs
  updateState(marks: any, attrs: any, stats: any, history: any, activeNodes: any) {
    this.delegate?.updateState(marks, attrs, stats, history, activeNodes);
    // Sync manualFontSize from active marks
    if (this.activeMarks && typeof this.activeMarks.fontSize === "string") {
      this.manualFontSize = this.activeMarks.fontSize;
    } else {
      this.manualFontSize = "";
    }
  },

  getStatusMessage(): string {
    const i18n = this.i18n || window.EDITOR_I18N;
    if (!i18n) return "";

    switch (this.status) {
      case "saving":
        return i18n.saving;
      case "saved":
        return i18n.saved;
      case "unsaved":
        return i18n.saving;
      case "offline":
        return i18n.offline;
      case "error":
        return i18n.error;
      default:
        return "";
    }
  },

  canUndo() {
    return this.delegate?.canUndo() ?? false;
  },
  canRedo() {
    return this.delegate?.canRedo() ?? false;
  },

  // Typography
  toggleBold() {
    this.delegate?.toggleBold();
  },
  toggleItalic() {
    this.delegate?.toggleItalic();
  },
  toggleUnderline() {
    this.delegate?.toggleUnderline();
  },
  toggleHighlight() {
    this.delegate?.toggleHighlight();
  },
  setColor(e: any) {
    this.delegate?.setColor(e);
  },

  // Alignment
  setTextAlign(align: string) {
    this.delegate?.setTextAlign(align);
  },

  // Headings
  toggleHeading(level: number) {
    this.delegate?.toggleHeading(level);
  },

  // Lists
  toggleBulletList() {
    this.delegate?.toggleBulletList();
  },
  toggleOrderedList() {
    this.delegate?.toggleOrderedList();
  },

  // Insert
  triggerImageUpload() {
    this.delegate?.triggerImageUpload();
  },
  insertTable(rows: number, cols: number) {
    this.delegate?.insertTable(rows, cols);
  },
  insertToC() {
    this.delegate?.insertToC();
  },

  // History
  undo() {
    this.delegate?.undo();
  },
  redo() {
    this.delegate?.redo();
  },

  // Link Handling
  openLinkModal() {
    this.delegate?.openLinkModal();
  },
  closeLinkModal() {
    this.linkModalOpen = false;
  },
  saveLink() {
    this.delegate?.saveLink();
  },

  // Table Actions
  mergeCells() {
    this.delegate?.mergeCells();
  },
  splitCell() {
    this.delegate?.splitCell();
  },
  deleteTable() {
    this.delegate?.deleteTable();
  },
  addColumnBefore() {
    this.delegate?.addColumnBefore();
  },
  addColumnAfter() {
    this.delegate?.addColumnAfter();
  },
  deleteColumn() {
    this.delegate?.deleteColumn();
  },
  addRowBefore() {
    this.delegate?.addRowBefore();
  },
  addRowAfter() {
    this.delegate?.addRowAfter();
  },
  deleteRow() {
    this.delegate?.deleteRow();
  },
  toggleHeaderRow() {
    this.delegate?.toggleHeaderRow();
  },
  toggleHeaderColumn() {
    this.delegate?.toggleHeaderColumn();
  },
  toggleHeaderCell() {
    this.delegate?.toggleHeaderCell();
  },
  setVerticalAlign(align: string) {
    this.delegate?.setVerticalAlign(align);
  },

  // Spacing
  unsetFontSize() {
    this.delegate?.unsetFontSize();
  },
  setLineHeight(height: string) {
    this.delegate?.setLineHeight(height);
  },
  setParagraphSpacing(spacing: string) {
    this.delegate?.setParagraphSpacing(spacing);
  },
  setFontSize(size: string) {
    if (!size || size.trim() === "") {
      this.unsetFontSize();
      return;
    }
    // Sanitation & Normalization
    let cleanSize = size.trim();
    if (/^\d+$/.test(cleanSize)) {
      cleanSize += "px";
    }
    this.delegate?.setFontSize(cleanSize);
  },

  // Image Actions
  setImageAlign(align: string) {
    this.delegate?.setImageAlign(align);
  },
  setImageWidth(width: string) {
    this.delegate?.setImageWidth(width);
  },
  setImageWrap(wrap: boolean) {
    this.delegate?.setImageWrap(wrap);
  },
  setImageMargin(margin: string) {
    this.delegate?.setImageMargin(margin);
  },
  deleteImage() {
    this.delegate?.deleteImage();
  },
} as EditorStore); // Validate against interface
